import { useState } from "react"
import { cn } from "@/lib/utils"

interface ClientAvatarProps {
  name: string
  domain: string | null
  website?: string | null // Profile website URL - takes priority for favicon
  logoUrl?: string | null // Uploaded client logo - takes highest priority
  size?: "sm" | "md" | "lg"
  className?: string
  gradient?: string
}

// Generate a consistent gradient based on client name
function getClientGradient(name: string) {
  const gradients = [
    "from-violet-500 to-purple-600",
    "from-blue-500 to-cyan-600",
    "from-teal-500 to-emerald-600",
    "from-orange-500 to-red-600",
    "from-pink-500 to-rose-600",
    "from-indigo-500 to-blue-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
  ]
  const index = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return gradients[index % gradients.length]
}

// Check if domain is a local/development domain that won't have a real favicon
function isLocalDomain(domain: string | null): boolean {
  if (!domain) return true

  const cleanDomain = domain
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0]
    .split(":")[0] // Remove port

  const localPatterns = [
    "localhost",
    "127.0.0.1",
    "0.0.0.0",
    /^192\.168\./,
    /^10\./,
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
    /\.local$/,
    /\.test$/,
    /\.localhost$/,
  ]

  return localPatterns.some(pattern =>
    typeof pattern === "string"
      ? cleanDomain === pattern || cleanDomain.startsWith(pattern + ":")
      : pattern.test(cleanDomain)
  )
}

// Get favicon URL using favicone.com service
function getFaviconUrl(domain: string | null): string | null {
  if (!domain) return null

  // Clean up domain - remove protocol and paths
  const cleanDomain = domain
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0]

  // Don't try to fetch favicon for local domains
  if (isLocalDomain(domain)) {
    return null
  }

  return `https://favicone.com/${cleanDomain}?s=64`
}

const sizeClasses = {
  sm: "h-6 w-6 text-xs",
  md: "h-10 w-10 text-lg",
  lg: "h-12 w-12 text-lg",
}

// URLs that have finished loading at least once this session. Components
// remount every time a popover reopens, which used to reset imgLoaded and
// replay the letter-placeholder flash + fade even for browser-cached images.
const loadedImageUrls = new Set<string>()

export function ClientAvatar({
  name,
  domain,
  website,
  logoUrl,
  size = "md",
  className,
  gradient: customGradient
}: ClientAvatarProps) {
  const gradient = customGradient || getClientGradient(name)
  const faviconUrl = website ? getFaviconUrl(website) : getFaviconUrl(domain)
  const initial = name.charAt(0).toUpperCase()

  const [imgError, setImgError] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(() => !!faviconUrl && loadedImageUrls.has(faviconUrl))
  const [logoError, setLogoError] = useState(false)

  const markFaviconLoaded = () => {
    if (faviconUrl) loadedImageUrls.add(faviconUrl)
    setImgLoaded(true)
  }

  // Priority: 1) Uploaded logo, 2) Favicon, 3) Letter fallback
  const showLogo = logoUrl && !logoError
  const showFavicon = !showLogo && faviconUrl && !imgError

  return (
    <div
      className={cn(
        "relative flex items-center justify-center rounded-lg bg-gradient-to-br text-white font-semibold shrink-0 overflow-hidden ring-1 ring-black/10 dark:ring-white/20",
        gradient,
        sizeClasses[size],
        className
      )}
    >
      {/* Always render the letter as background/fallback */}
      <span className={cn(
        "absolute inset-0 flex items-center justify-center transition-opacity duration-200",
        ((imgLoaded && showFavicon) || showLogo) && "opacity-0"
      )}>
        {initial}
      </span>

      {/* Try to load uploaded logo first */}
      {showLogo && (
        <img
          src={logoUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setLogoError(true)}
        />
      )}

      {/* Fall back to favicon. The ref check catches browser-cached images
          that are already complete on mount, so they render instantly with
          no placeholder flash or fade. */}
      {showFavicon && (
        <img
          ref={(el) => {
            if (el && el.complete && el.naturalWidth > 0 && !imgLoaded) markFaviconLoaded()
          }}
          src={faviconUrl}
          alt=""
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            imgLoaded ? "opacity-100" : "opacity-0 transition-opacity duration-200"
          )}
          onLoad={markFaviconLoaded}
          onError={() => setImgError(true)}
        />
      )}
    </div>
  )
}

// Export the gradient function for use elsewhere
export { getClientGradient }
